import { Button } from '@/components/ui/button';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { fill, type LeaderboardCopy } from '../leaderboard-copy';

interface LeaderboardPaginationProps {
  copy: LeaderboardCopy;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const SIZES = [15, 30, 50] as const;

export function LeaderboardPagination({ copy, page, pageSize, total, onPageChange, onPageSizeChange }: LeaderboardPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-4 py-3">
      <div role="group" aria-label={copy.perPage} className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">{copy.perPage}</span>
        <div className="inline-flex rounded-md border border-border/60 bg-surface-2 p-0.5">
          {SIZES.map((size) => (
            <button
              key={size}
              type="button"
              aria-pressed={pageSize === size}
              onClick={() => onPageSizeChange(size)}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                pageSize === size ? 'bg-surface text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {size}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="size-9" onClick={() => onPageChange(page - 1)} disabled={page <= 1} aria-label={copy.previous}>
          <Icon name="chevronLeft" />
        </Button>
        <span className="px-1 text-xs tabular-nums text-muted-foreground">{fill(copy.pageOf, { page, pages: totalPages })}</span>
        <Button variant="ghost" size="icon" className="size-9" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} aria-label={copy.next}>
          <Icon name="chevronRight" />
        </Button>
      </div>
    </div>
  );
}
